import React, { useEffect, useState } from "react";
import store from "../../redux/store";
import {
  Card,
  Flex,
  Heading,
  Input,
  InputGroup,
  InputRightElement,
  Select,
  SimpleGrid,
  Spacer,
  Text,
  Button,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Box,
  Badge,
} from "@chakra-ui/react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  useDisclosure,
  useToast,
  Spinner,
} from "@chakra-ui/react";
import { FiChevronLeft, FiChevronRight, FiSearch } from "react-icons/fi";
import { useDispatch, useSelector } from "react-redux";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import CustomDataCard from "../../components/CustomDataCard";
import { formatCurrency } from "../../utils/utils";
import { get_all_customer_list } from "../../redux/customerSlice";
import { get_receipt_list, delete_receipt, add_receipt, update_payment } from "../../redux/ReceiptNewSlice";
import { Menu, MenuButton, MenuList, MenuItem } from "@chakra-ui/react";
import { ChevronDown } from "lucide-react"; // or from "@chakra-ui/icons" if you prefer
import { get_bank_list } from "../../redux/bankSlice";
import GasCylinderLoader from "../../components/GasCylinderLoader";

// Validation Schema
const receiptValidationSchema = Yup.object().shape({
  userid: Yup.string()
    .required("Customer selection is required"),
  voucher_date: Yup.date()
    .required("Voucher date is required")
    .max(new Date(), "Voucher date cannot be in the future"),
  amount: Yup.number()
    .required("Amount is required")
    .positive("Amount must be positive")
    .min(0.01, "Amount must be at least 0.01"),
  payment_mode: Yup.string()
    .required("Payment mode is required")
    .oneOf(["1", "2"], "Invalid payment mode"),
  transaction_number: Yup.string()
    .when("payment_mode", {
      is: "2",
      then: (schema) => schema.required("Transaction number is required for online payments"),
      otherwise: (schema) => schema.notRequired(),
    }),
  mode_of_transaction: Yup.string()
    .when("payment_mode", {
      is: "2",
      then: (schema) => schema.required("Mode of transaction is required for online payments"),
      otherwise: (schema) => schema.notRequired(),
    }),
  narration: Yup.string()
    .max(500, "Narration must be less than 500 characters"),
  orderid: Yup.string()
    .required("At least one Order ID is required"),
  bank_id: Yup.string()
    .when("payment_mode", {
      is: "2",
      then: (schema) => schema.required("Bank selection is required for online payments"),
      otherwise: (schema) => schema.notRequired(),
    }),
});

const Receipt = () => {
  const [rows, setRows] = useState(5);
  const [page, setPage] = useState(1);
  const [initialValue, setInitialValue] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [customerSearchTerm, setCustomerSearchTerm] = useState("");
  const [showForm, setShowForm] = useState(false);
  const dispatch = useDispatch();
  const [isCustomerLoading, setIsCustomerLoading] = useState(false);
  const {
    isOpen: isAddOpen,
    onOpen: onAddOpen,
    onClose: onAddClose,
  } = useDisclosure();

  const {
    isOpen: isApproveOpen,
    onOpen: onApproveOpen,
    onClose: onApproveClose,
  } = useDisclosure();

  const [selectedReceipt, setSelectedReceipt] = useState(null);
  // const [loading, setLoading] = useState(false);
  const toast = useToast();
  const [selectedBank, setSelectedBank] = useState("");
  const [filterReceiptID, setFilterReceiptID] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterCustomer, setFilterCustomer] = useState("");
  const [filterOrder, setFilterOrder] = useState("");
  const [filterVoucherDate, setFilterVoucherDate] = useState("");

  // State for orders
  const [orders, setOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(false);

  const { bank_list, total_bank_count, hasCashInSystem } = useSelector(
    (state) => state.banks
  );

  useEffect(() => {
    dispatch(get_bank_list({ page: 1, rows: 1000 })); 
    setInitialValue(1);
  }, [dispatch]);

  const initialFormValues = {
    userid: "",
    voucher_date: "",
    amount: "",
    payment_mode: "",
    transaction_number: "",
    mode_of_transaction: "",
    narration: "",
    orderid: "",
    bank_id: "",
  };

  const { receipt_list, total_receipt_count,loading } = useSelector(
    (state) => state.receipts
  );
  const { all_customer_list, all_loading } = useSelector(
    (state) => state.customers
  );

  const getModeOfTransactionName = (mode) => {
    switch (String(mode)) {
      case "1":
        return "Phonepay";
      case "2":
        return "GPay";
      case "3":
        return "Paytm";
      case "4":
        return "Cred";
      case "5":
        return "RTGS";
        case "6":
          return "NEFT";
          case "7":
            return "IMPS";
            case "8":
              return "Others";
      default:
        return "N/A";
    }
  };


  useEffect(() => {
    store.dispatch(get_receipt_list({ page: page, limit: rows }));
  }, [page, rows]);

  const handleShowForm = () => {
    if (!showForm) {
      setIsCustomerLoading(true);
      store.dispatch(get_all_customer_list())
        .finally(() => setIsCustomerLoading(false));
    }
    setShowForm(!showForm);
  };

  // Fetch orders when customer is selected
  const fetchOrdersForCustomer = async (userid) => {
    if (!userid) {
      setOrders([]);
      return;
    }

    setLoadingOrders(true);
    try {
      const formData = new FormData();
      formData.append("action", "order_list");
      formData.append("userid", userid);

      const response = await fetch("https://dev.gaswale.com/api/admin/receipt.php", {
        method: "POST",
        body: formData,
      });

      const data = await response.json();
      if (data.success && data.data) {
        setOrders(data.data);
      } else {
        setOrders([]);
      }
    } catch (error) {
      console.error("Error fetching orders:", error);
      toast({
        title: "Error fetching orders",
        status: "error",
        duration: 2000,
        isClosable: true,
        position: "top-right",
      });
      setOrders([]);
    } finally {
      setLoadingOrders(false);
    }
  };

  // Handle customer selection change
  const handleCustomerChange = (e, setFieldValue) => {
    const userid = e.target.value;
    setFieldValue("userid", userid);
    setFieldValue("orderid", "");
    setFieldValue("amount", "");
    fetchOrdersForCustomer(userid);
  };

  // Handle order selection and auto-fetch amount
  const handleOrderChange = (e, setFieldValue) => {
    const orderid = e.target.value;
    setFieldValue("orderid", orderid);

    if (orderid) {
      const selectedOrder = orders.find((order) => order.orderid === orderid);
      if (selectedOrder && selectedOrder.details && selectedOrder.details.length > 0) {
        const totalDiscountedAmount = selectedOrder.details.reduce((sum, item) => {
          const val = parseFloat(item.discounted_amount) || 0;
          return sum + val;
        }, 0);
        setFieldValue("amount", totalDiscountedAmount.toFixed(2));
      } else {
        setFieldValue("amount", "");
      }
    } else {
      setFieldValue("amount", "");
    }
  };

  const handleApproveClick = (receipt) => {
    setSelectedReceipt(receipt);
    onApproveOpen();
  };

  const handleRejectClick = (receipt) => {
    console.log("Rejected:", receipt.id);
  };

  const handleApproveSubmit = async () => {
    // Validate bank selection for online payments
    if (selectedReceipt.payment_mode.toLowerCase() === "online" && !selectedBank) {
      toast({
        title: "Please select a bank for online payment.",
        status: "warning",
        duration: 2000,
        isClosable: true,
        position: "top-right",
      });
      return;
    }

    // setLoading(true);

    const payload = {
      id: selectedReceipt.id,
      userid: selectedReceipt.userid,
      amount: parseFloat(selectedReceipt.amount),
      payment_mode: selectedReceipt.payment_mode.toLowerCase() === "cash" ? "1" : "2",
      orderid: selectedReceipt.orders?.map((order) => order.orderid) || [],
      bank_id: selectedReceipt.payment_mode.toLowerCase() === "online" ? selectedBank : "",
    };

    try {
      const res = await store.dispatch(update_payment(payload)).unwrap();
      toast({
        title: "Receipt approved successfully!",
        status: "success",
        duration: 2000,
        isClosable: true,
        position: "top-right",
      });
      onApproveClose();
      store.dispatch(get_receipt_list({ page, limit: rows }));
    } catch (error) {
      toast({
        title: error || "Approval failed",
        status: "error",
        duration: 2000,
        isClosable: true,
        position: "top-right",
      });
    } finally {
      // setLoading(false);
    }
  };

  const onPrevious = () => {
    if (page > 1) {
      setPage(page - 1);
      setInitialValue(initialValue - rows);
    }
  };

  const onNext = () => {
    if (rows * page < total_receipt_count) {
      setPage(page + 1);
      setInitialValue(initialValue + rows);
    }
  };

  const handleDelete = (id) => {
    store.dispatch(delete_receipt(id));
  };

  const handleAddReceipt = async (values, { setSubmitting, resetForm }) => {
    try {
      const payload = {
        userid: values.userid,
        voucher_date: values.voucher_date,
        amount: parseFloat(values.amount) || 0,
        payment_mode: values.payment_mode,
        transaction_number: values.transaction_number || "",
        mode_of_transaction: values.mode_of_transaction || "",
        narration: values.narration || "",
        orderid: values.orderid,
        bank_id: values.payment_mode === "2" ? values.bank_id : "",
      };
      console.log("Final payload:", payload);

      await store.dispatch(add_receipt(payload)).unwrap();

      toast({
        title: "Receipt added successfully!",
        status: "success",
        duration: 2000,
        isClosable: true,
        position: "top-right",
      });

      resetForm();
      setShowForm(false);
      setOrders([]);
      await store.dispatch(get_receipt_list({ page, limit: rows }));
    } catch (error) {
      toast({
        title: error || "Error adding receipt",
        status: "error",
        duration: 2000,
        isClosable: true,
        position: "top-right",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const getBankNameById = (bank_id) => {
    if (!bank_id) return "N/A";
    const bank = bank_list.find(bank => bank.value == bank_id);
    return bank ? bank.bankname : "N/A";
  };

  const getPaymentModeName = (payment_mode) => {
    if (!payment_mode) return "N/A";
    const mode = payment_mode.toString().toLowerCase();
    if (mode === "1" || mode === "cash") return "Cash";
    if (mode === "2" || mode === "online") return "Online";
    return payment_mode;
  };

  const formatDate = (dateString) => {
    if (!dateString || dateString === "0000-00-00 00:00:00") return "N/A";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString();
    } catch {
      return "N/A";
    }
  };

  const createReceiptCardData = (receipt) => {
    const isOnline = getPaymentModeName(receipt.payment_mode)?.toLowerCase() === "online";
  
    const data = [
      { key: "Receipt ID", value: receipt.sr_no || "N/A" },
      {
        key: "Orders Id",
        value: receipt.orders?.map((order) => order.orderid).join(", ") || "N/A",
      },
      {
        key: "Status",
        value: (
          <Badge
            colorScheme={
              receipt.status === "1"
                ? "green"
                : receipt.status === "0"
                ? "yellow"
                : "red"
            }
            px={3}
            py={1}
            borderRadius="md"
            fontSize="0.8rem"
            textTransform="uppercase"
          >
            {receipt.status === "1"
              ? "APPROVED"
              : receipt.status === "2"
              ? "REJECTED"
              : "PENDING"}
          </Badge>
        ),
      },
      { key: "Customer Name", value: receipt.customer_name || "N/A" },
      { key: "Voucher Date", value: formatDate(receipt.voucher_date) },
      { key: "Amount", value: formatCurrency(receipt.amount) },
      { key: "To Received", value: receipt.paid_to || "N/A" },
      { key: "Payment Mode", value: getPaymentModeName(receipt.payment_mode) },
      { key: "Cash Received", value: receipt.cash_received ? `₹${receipt.cash_received}` : "N/A" },
      { key: "Bank", value: getBankNameById(receipt.bank_id) },
      { key: "Narration", value: receipt.narration || "N/A" },
    ];
  
    // ✅ Conditionally add fields for online payments only
    if (isOnline) {
      data.splice(8, 0, // insert after Payment Mode (index 8)
        { key: "Transaction Number", value: receipt.transaction_number || "N/A" },
        {
          key: "Mode of Transaction",
          value: getModeOfTransactionName(receipt.mode_of_transaction) || "N/A",
        },
        {
          key: "Online Received",
          value: receipt.online_received ? `₹${receipt.online_received}` : "N/A",
        }
      );
    }
  
    return data;
  };
  

  const filteredReceipts = (receipt_list || []).filter((receipt) => {
    const matchesSearch =
      receipt.sr_no?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      receipt.customer_name?.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesReceiptID = filterReceiptID
      ? receipt.sr_no?.toString().toLowerCase().includes(filterReceiptID.toLowerCase())
      : true;

    const matchesStatus = filterStatus
      ? receipt.status === filterStatus
      : true;

    const matchesCustomer = filterCustomer
      ? receipt.customer_name?.toLowerCase().includes(filterCustomer.toLowerCase())
      : true;

    const matchesOrder = filterOrder
      ? receipt.orders?.some((order) =>
        order.orderid?.toLowerCase().includes(filterOrder.toLowerCase())
      )
      : true;

    const matchesVoucherDate = filterVoucherDate
      ? receipt.voucher_date === filterVoucherDate
      : true;

    return (
      matchesSearch &&
      matchesReceiptID &&
      matchesStatus &&
      matchesCustomer &&
      matchesOrder &&
      matchesVoucherDate
    );
  });

  return (
    <>
      {/* Top Bar */}
      <Card
        bg="#fff"
        paddingInline={5}
        paddingBlock={2}
        mb={4}
        w="100%"
      >
        <Flex
          w="100%"
          direction={["column", "column", "row"]}
          align="center"
          justify="space-between"
          gap={4}
          flexWrap="wrap"
        >
          <Heading fontSize="xl">Receipts</Heading>

          <Flex
            direction={["column", "column", "row"]}
            align="center"
            gap={3}
            flexWrap="wrap"
          >
            <Select
              size="md"
              value={rows}
              onChange={(e) => {
                setRows(Number(e.target.value));
                setPage(1);
                setInitialValue(1);
              }}
              w={["100%", "100%", "auto"]}
            >
              <option value="5">5</option>
              <option value="10">10</option>
              <option value="25">25</option>
              <option value="50">50</option>
              <option value="100">100</option>
            </Select>

            <InputGroup
              bg="#f4f5f7"
              borderRadius={5}
              w={["100%", "100%", "auto"]}
              maxW="sm"
            >
              <Input
                placeholder="Search ....."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <InputRightElement>
                <FiSearch />
              </InputRightElement>
            </InputGroup>

            <Button
              w={["100%", "100%", "auto"]}
              px={6}
              py={2.5}
              fontSize="sm"
              colorScheme="blue"
              onClick={() => {
                setIsCustomerLoading(true);
                store.dispatch(get_all_customer_list())
                  .finally(() => setIsCustomerLoading(false));
                onAddOpen();
              }}
              isLoading={isCustomerLoading || all_loading}
            >
              Add Receipt
            </Button>

          </Flex>
        </Flex>
      </Card>

      {loading ? (<GasCylinderLoader size="sm" loadingText="Loading Receipts"/>):(
  <>
   {/* Receipt List */}
   <SimpleGrid minChildWidth="xs" spacing="10px">
        {filteredReceipts?.map((receipt) => (
          <Box key={receipt.id}>
            <CustomDataCard
              data={createReceiptCardData(receipt)}
              keyWidth="130px"
              keyColor="gray.700"
              valueColor="gray.600"
              hoverEffect={true}
            />

            {receipt.status === "0" && (
              <Flex justify="center" gap={3} mt={2}>
                <Button
                  colorScheme="green"
                  size="sm"
                  onClick={() => handleApproveClick(receipt)}
                >
                  Approve
                </Button>
                <Button colorScheme="red" size="sm" onClick={() => handleRejectClick(receipt)}>
                  Reject
                </Button>
              </Flex>
            )}
          </Box>
        ))}
      </SimpleGrid>

      {/* Approve Modal */}
      <Modal isOpen={isApproveOpen} onClose={onApproveClose} isCentered size="lg">
        <ModalOverlay />
        <ModalContent borderRadius="xl">
          <ModalHeader>Approve Receipt</ModalHeader>

          <ModalBody>
            {/* Receipt Details Card */}
            {selectedReceipt && (
              <Card variant="outline" borderRadius="xl" shadow="md" p={4} mb={4}>
                <SimpleGrid columns={2} spacing={3}>
                  {/* Receipt ID */}
                  <Box>
                    <Text fontWeight="medium">Receipt ID:</Text>
                    <Text color="gray.600">{selectedReceipt.sr_no || "N/A"}</Text>
                  </Box>

                  {/* Customer Name */}
                  <Box>
                    <Text fontWeight="medium">Customer:</Text>
                    <Text color="gray.600">{selectedReceipt.customer_name || "N/A"}</Text>
                  </Box>

                  {/* Voucher Date */}
                  <Box>
                    <Text fontWeight="medium">Voucher Date:</Text>
                    <Text color="gray.600">{formatDate(selectedReceipt.voucher_date)}</Text>
                  </Box>

                  {/* Amount */}
                  <Box>
                    <Text fontWeight="medium">Amount:</Text>
                    <Text color="green.600" fontWeight="semibold">
                      ₹{parseFloat(selectedReceipt.amount).toFixed(2)}
                    </Text>
                  </Box>

                  {/* Payment Mode */}
                  <Box>
                    <Text fontWeight="medium">Payment Mode:</Text>
                    <Text color="gray.600">
                      {getPaymentModeName(selectedReceipt.payment_mode)}
                    </Text>
                  </Box>

                  {/* Cash Received (Only for Cash) */}
                  {selectedReceipt.payment_mode?.toLowerCase() === "cash" && (
                    <Box>
                      <Text fontWeight="medium">Cash Received:</Text>
                      <Text color="gray.600">
                        ₹{parseFloat(selectedReceipt.cash_received || 0).toFixed(2)}
                      </Text>
                    </Box>
                  )}

                  {/* Online Received (Only for Online) */}
                  {selectedReceipt.payment_mode?.toLowerCase() === "online" && (
                    <Box>
                      <Text fontWeight="medium">Online Received:</Text>
                      <Text color="gray.600">
                        ₹{parseFloat(selectedReceipt.online_received || 0).toFixed(2)}
                      </Text>
                    </Box>
                  )}

                  {/* Mode of Transaction */}
                  <Flex justify="space-between" py={2}>
                    <Text fontWeight="medium">Mode of Transaction:</Text>
                    <Text>{getModeOfTransactionName(selectedReceipt?.mode_of_transaction)}</Text>
                  </Flex>


                  {/* Transaction Number (Only for Online) */}
                  {selectedReceipt.payment_mode?.toLowerCase() === "online" && (
                    <Box>
                      <Text fontWeight="medium">Transaction Number:</Text>
                      <Text color="gray.600">{selectedReceipt.transaction_number || "N/A"}</Text>
                    </Box>
                  )}

                  {/* Paid To */}
                  <Box>
                    <Text fontWeight="medium">Paid To:</Text>
                    <Text color="gray.600">{selectedReceipt.paid_to || "N/A"}</Text>
                  </Box>

                  {/* Narration */}
                  <Box gridColumn="span 2">
                    <Text fontWeight="medium">Narration:</Text>
                    <Text color="gray.600">{selectedReceipt.narration || "N/A"}</Text>
                  </Box>

                  {/* Orders */}
                  <Box gridColumn="span 2">
                    <Text fontWeight="medium">Orders:</Text>
                    <Text color="gray.600">
                      {selectedReceipt.orders?.map((order) => order.orderid).join(", ") || "N/A"}
                    </Text>
                  </Box>
                </SimpleGrid>
              </Card>
            )}

            {/* Show Bank Select only for Online Payment Mode */}
            {selectedReceipt && selectedReceipt.payment_mode?.toLowerCase() === "online" && (
              <Box mb={4}>
                <Text mb={1} fontWeight="medium">
                  Select Admin Bank *
                </Text>
                <Select
                  placeholder="Select Bank"
                  value={selectedBank}
                  onChange={(e) => setSelectedBank(e.target.value)}
                >
                  {bank_list.map((bank) => (
                    <option key={bank.value} value={bank.value}>
                      {bank.bankname} - ({bank.bank_branch}) - {bank.accountno}
                    </option>
                  ))}
                </Select>
              </Box>
            )}
          </ModalBody>

          <ModalFooter>
            <Button variant="ghost" onClick={onApproveClose} mr={3}>
              Cancel
            </Button>
            <Button
              colorScheme="green"
              onClick={handleApproveSubmit}
              isLoading={loading}
            >
              Approve
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Add Receipt Modal */}
      {/* ✅ Add Receipt Modal */}
      {/* ✅ Add Receipt Modal */}
      <Modal isOpen={isAddOpen} onClose={onAddClose} size="xl" isCentered scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent borderRadius="xl" maxW="700px">
          <ModalHeader>Add New Receipt</ModalHeader>
          <ModalBody>
            <Formik
              initialValues={{
                userid: "",
                voucher_date: "",
                amount: "",
                payment_mode: "",
                transaction_number: "",
                mode_of_transaction: "",
                narration: "",
                orderid: "",
                bank_id: "",
              }}
              validationSchema={receiptValidationSchema}
              onSubmit={async (values, { setSubmitting, resetForm }) => {
                await handleAddReceipt(values, { setSubmitting, resetForm });
                onAddClose();
              }}
            >
              {({ isSubmitting, values, setFieldValue }) => (
                <Form>
                  <Flex direction="column" gap={4}>
             {/* ✅ Searchable Customer Dropdown */}
<Field name="userid">
  {({ field, form }) => {
    const filteredCustomers = all_customer_list?.filter((customer) =>
      customer.name.toLowerCase().includes(customerSearchTerm.toLowerCase())
    );

    return (
      <FormControl isInvalid={form.errors.userid && form.touched.userid}>
        <FormLabel>Customer *</FormLabel>
        <Menu closeOnSelect>
          <MenuButton
            as={Button} 
            w="100%"
            justifyContent="space-between"
            rightIcon={<ChevronDown />}
            variant="outline"
            isDisabled={isCustomerLoading || all_loading}
          >
            {values.userid
              ? all_customer_list.find((c) => c.id === values.userid)?.name || "Select Customer"
              : "Select Customer"}
          </MenuButton>
          <MenuList maxH="250px" overflowY="auto" p={2}>
            {/* Search Box */}
            <Input
              placeholder="Search customer..."
              size="sm"
              mb={2}
              value={customerSearchTerm}
              onChange={(e) => setCustomerSearchTerm(e.target.value)}
              autoFocus
            />
            {/* Filtered Results */}
            {filteredCustomers?.length > 0 ? (
              filteredCustomers.map((customer) => (
                <MenuItem
                  key={customer.id}
                  onClick={() => {
                    setFieldValue("userid", customer.id);
                    handleCustomerChange({ target: { value: customer.id } }, setFieldValue);
                  }}
                >
                  {customer.name}
                </MenuItem>
              ))
            ) : (
              <Text fontSize="sm" color="gray.500" p={2}>
                No results found
              </Text>
            )}
          </MenuList>
        </Menu>
        <FormErrorMessage>{form.errors.userid}</FormErrorMessage>
      </FormControl>
    );
  }}
</Field>



                    {/* Order ID */}
                    <Field name="orderid">
                      {({ field, form }) => (
                        <FormControl isInvalid={form.errors.orderid && form.touched.orderid}>
                          <FormLabel>Order *</FormLabel>
                          <Select
                            placeholder={loadingOrders ? "Loading..." : "Select Order"}
                            {...field}
                            onChange={(e) => handleOrderChange(e, setFieldValue)}
                            isDisabled={loadingOrders || !values.userid}
                          >
                            {orders.map((order) => (
                              <option key={order.orderid} value={order.orderid}>
                                {order.orderid}
                              </option>
                            ))}
                          </Select>
                          <FormErrorMessage>{form.errors.orderid}</FormErrorMessage>
                        </FormControl>
                      )}
                    </Field>

                    {/* Voucher Date */}
                    <Field name="voucher_date">
                      {({ field, form }) => (
                        <FormControl isInvalid={form.errors.voucher_date && form.touched.voucher_date}>
                          <FormLabel>Voucher Date *</FormLabel>
                          <Input type="date" {...field} />
                          <FormErrorMessage>{form.errors.voucher_date}</FormErrorMessage>
                        </FormControl>
                      )}
                    </Field>

                    {/* Amount */}
                    <Field name="amount">
                      {({ field, form }) => (
                        <FormControl isInvalid={form.errors.amount && form.touched.amount}>
                          <FormLabel>Amount *</FormLabel>
                          <Input type="number" {...field} placeholder="Enter amount" />
                          <FormErrorMessage>{form.errors.amount}</FormErrorMessage>
                        </FormControl>
                      )}
                    </Field>

                    {/* Payment Mode */}
                    <Field name="payment_mode">
                      {({ field, form }) => (
                        <FormControl isInvalid={form.errors.payment_mode && form.touched.payment_mode}>
                          <FormLabel>Payment Mode *</FormLabel>
                          <Select
                            {...field}
                            placeholder="Select Mode"
                            onChange={(e) => {
                              setFieldValue("payment_mode", e.target.value);
                              // Reset conditional fields
                              setFieldValue("transaction_number", "");
                              setFieldValue("mode_of_transaction", "");
                              setFieldValue("bank_id", "");
                            }}
                          >
                            <option value="1">Cash</option>
                            <option value="2">Online</option>
                          </Select>
                          <FormErrorMessage>{form.errors.payment_mode}</FormErrorMessage>
                        </FormControl>
                      )}
                    </Field>

                    {/* Conditional Fields for Online Mode */}
                    {values.payment_mode === "2" && (
                      <>
                        {/* Mode of Transaction */}
                        <Field name="mode_of_transaction">
                          {({ field, form }) => (
                            <FormControl
                              isInvalid={form.errors.mode_of_transaction && form.touched.mode_of_transaction}
                            >
                              <FormLabel>Mode of Transaction *</FormLabel>
                              <Select {...field} placeholder="Select Mode">
                              <option value="1">Phonepay</option>
                                <option value="2">GPay</option>
                                <option value="3">Paytm</option>
                                <option value="4">Cred</option>
                                <option value="5">RTGS</option>
                                <option value="6">NEFT</option>
                                <option value="7">IMPS</option>
                                <option value="8">others</option>
                              </Select>
                              <FormErrorMessage>{form.errors.mode_of_transaction}</FormErrorMessage>
                            </FormControl>
                          )}
                        </Field>

                        {/* Transaction Number */}
                        <Field name="transaction_number">
                          {({ field, form }) => (
                            <FormControl
                              isInvalid={form.errors.transaction_number && form.touched.transaction_number}
                            >
                              <FormLabel>Transaction Number *</FormLabel>
                              <Input {...field} placeholder="Enter transaction number" />
                              <FormErrorMessage>{form.errors.transaction_number}</FormErrorMessage>
                            </FormControl>
                          )}
                        </Field>

                        {/* Bank */}
                        <Field name="bank_id">
                          {({ field, form }) => (
                            <FormControl isInvalid={form.errors.bank_id && form.touched.bank_id}>
                              <FormLabel>Select Bank *</FormLabel>
                              <Select {...field} placeholder="Select Bank">
                                {bank_list.map((bank) => (
                                  <option key={bank.value} value={bank.value}>
                                    {bank.bankname} - ({bank.bank_branch}) - {bank.accountno}
                                  </option>
                                ))}
                              </Select>
                              <FormErrorMessage>{form.errors.bank_id}</FormErrorMessage>
                            </FormControl>
                          )}
                        </Field>
                      </>
                    )}

                    {/* Narration */}
                    <Field name="narration">
                      {({ field, form }) => (
                        <FormControl isInvalid={form.errors.narration && form.touched.narration}>
                          <FormLabel>Narration</FormLabel>
                          <Input {...field} placeholder="Enter narration (optional)" />
                          <FormErrorMessage>{form.errors.narration}</FormErrorMessage>
                        </FormControl>
                      )}
                    </Field>

                    <Button
                      type="submit"
                      colorScheme="green"
                      isLoading={isSubmitting}
                      loadingText="Submitting..."
                    >
                      Submit
                    </Button>
                  </Flex>
                </Form>
              )}
            </Formik>
          </ModalBody>

          <ModalFooter>
            <Button variant="ghost" onClick={onAddClose}>
              Cancel
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>


      {/* Pagination */}
      <Card
        alignItems={"center"}
        gap={3}
        flexDirection={"row"}
        mt={4}
        justifyContent={"center"}
        padding={3}
      >
        <FiChevronLeft
          cursor={"pointer"}
          color={page === 1 ? "gray" : "#007bc9"}
          size={35}
          onClick={onPrevious}
        />
        <Flex gap={3} alignItems={"center"}>
          <Text>{initialValue}</Text>-
          <Text>{Math.min(rows * page, total_receipt_count)}</Text>
          <Text>of</Text>
          <Text>{total_receipt_count}</Text>
        </Flex>
        <FiChevronRight
          cursor={"pointer"}
          color={rows * page >= total_receipt_count ? "gray" : "#007bc9"}
          size={35}
          onClick={onNext}
        />
      </Card>
  </>
)}
     
    </>
  );
};

export default Receipt;